import React, { useEffect, useMemo, useRef } from 'react';
import {
  SubscriptionTrackerUser,
  TeamSubscriptionSummary,
} from '../../../types/admin/subscriptionTracker';
import { InlineTeamPlanDetails } from './InlineUserPlanDetails';

interface UserSubTableProps {
  users: SubscriptionTrackerUser[];
  teamSummaries: Record<string, TeamSubscriptionSummary>;
  loadingTeamIds?: Record<number, boolean>;
  pendingTeamIds?: Record<number, boolean>;
  onRequestTeamSummary: (teamId: number) => void;
  onViewPlan: (user: SubscriptionTrackerUser) => void;
}

function formatSeat(seatNo: number | null): string {
  if (seatNo == null) return '—';
  return String(seatNo);
}

function formatEmail(email: string): string {
  return email?.trim() ? email : '—';
}

function seatSortValue(seatNo: number | null): number {
  return seatNo == null ? Number.POSITIVE_INFINITY : seatNo;
}

function resolveTeamSummary(
  teamSummaries: Record<string, TeamSubscriptionSummary>,
  teamId: number
): TeamSubscriptionSummary | undefined {
  return teamSummaries[String(teamId)] ?? (teamSummaries as Record<number, TeamSubscriptionSummary>)[teamId];
}

type TeamGroup = {
  teamId: number;
  teamName: string;
  users: SubscriptionTrackerUser[];
};

function groupUsersByTeam(users: SubscriptionTrackerUser[]): TeamGroup[] {
  const groups: TeamGroup[] = [];
  const indexByTeamId = new Map<number, number>();

  for (const user of users) {
    const existing = indexByTeamId.get(user.team_id);
    if (existing == null) {
      indexByTeamId.set(user.team_id, groups.length);
      groups.push({
        teamId: user.team_id,
        teamName: user.team_name,
        users: [user],
      });
    } else {
      groups[existing].users.push(user);
    }
  }

  for (const group of groups) {
    group.users.sort((a, b) => {
      const seatDiff = seatSortValue(a.seat_no) - seatSortValue(b.seat_no);
      if (seatDiff !== 0) return seatDiff;
      return a.username.localeCompare(b.username);
    });
  }

  return groups;
}

function shouldSkipSummaryRequest(
  teamId: number,
  teamSummaries: Record<string, TeamSubscriptionSummary>,
  loadingTeamIds: Record<number, boolean>,
  pendingTeamIds: Record<number, boolean>
): boolean {
  if (resolveTeamSummary(teamSummaries, teamId)) return true;
  if (loadingTeamIds[teamId]) return true;
  if (pendingTeamIds[teamId]) return true;
  return false;
}

const UserSubTable: React.FC<UserSubTableProps> = ({
  users,
  teamSummaries,
  loadingTeamIds = {},
  pendingTeamIds = {},
  onRequestTeamSummary,
  onViewPlan,
}) => {
  const teams = useMemo(() => groupUsersByTeam(users), [users]);
  const containerRef = useRef<HTMLDivElement>(null);
  const onRequestRef = useRef(onRequestTeamSummary);
  onRequestRef.current = onRequestTeamSummary;

  const teamSummariesRef = useRef(teamSummaries);
  teamSummariesRef.current = teamSummaries;
  const loadingTeamIdsRef = useRef(loadingTeamIds);
  loadingTeamIdsRef.current = loadingTeamIds;
  const pendingTeamIdsRef = useRef(pendingTeamIds);
  pendingTeamIdsRef.current = pendingTeamIds;

  useEffect(() => {
    const root = containerRef.current;
    if (!root || teams.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const raw = el.dataset.teamId;
          const teamId = raw != null ? Number(raw) : NaN;
          if (!Number.isFinite(teamId)) continue;

          if (
            shouldSkipSummaryRequest(
              teamId,
              teamSummariesRef.current,
              loadingTeamIdsRef.current,
              pendingTeamIdsRef.current
            )
          ) {
            if (resolveTeamSummary(teamSummariesRef.current, teamId)) {
              observer.unobserve(el);
            }
            continue;
          }

          onRequestRef.current(teamId);
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0,
      }
    );

    const sections = root.querySelectorAll<HTMLElement>('section[data-team-id]');
    sections.forEach((section) => {
      const teamId = Number(section.dataset.teamId);
      if (
        Number.isFinite(teamId) &&
        resolveTeamSummary(teamSummaries, teamId)
      ) {
        return;
      }
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, [teams, teamSummaries]);

  if (users.length === 0) {
    return (
      <div className="text-center p-12 bg-gray-50 rounded-lg text-gray-500">
        No users found
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-4">
      {teams.map((team) => {
        const summary = resolveTeamSummary(teamSummaries, team.teamId);
        const teamLoading = Boolean(loadingTeamIds[team.teamId]);
        const teamPending = Boolean(pendingTeamIds[team.teamId]);
        const seatCount = team.users.length;
        const seatLabel = `${seatCount} seat${seatCount === 1 ? '' : 's'}`;
        const activeSubs = summary?.active_sub_count ?? 0;
        const accentClass =
          teamLoading || teamPending
            ? 'before:bg-slate-300'
            : activeSubs > 0
              ? 'before:bg-emerald-500'
              : 'before:bg-rose-400';

        return (
          <section
            key={team.teamId}
            data-team-id={team.teamId}
            className={`relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm before:absolute before:inset-y-0 before:left-0 before:w-1 ${accentClass}`}
          >
            <header className="border-b border-gray-100 px-4 py-4 pl-5">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-bold text-gray-900">{team.teamName}</h3>
                <span className="shrink-0 text-sm text-gray-500">{seatLabel}</span>
              </div>
              <InlineTeamPlanDetails
                summary={summary}
                summariesLoading={teamLoading}
                summaryPending={!summary && teamPending}
              />
            </header>
            <ul className="divide-y divide-gray-100">
              {team.users.map((user) => (
                <li key={user.user_id} className="px-4 py-3 pl-5 hover:bg-gray-50/80">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-sm font-semibold text-gray-900">{user.username}</span>
                      <span className="text-sm text-gray-500">{formatEmail(user.email)}</span>
                      <span className="text-xs text-gray-400">· Seat {formatSeat(user.seat_no)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onViewPlan(user)}
                      className="shrink-0 text-sm font-medium text-violet-600 hover:text-violet-800 hover:underline"
                    >
                      View plan
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default UserSubTable;
